import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import React, { useState } from "react";
import { downloadRaterWorkbook } from "../../../api/routes";
import {
  RatePrecheckWarning,
  ReportJobBatchEntry,
} from "../../../client/types.gen";
import { useRatePrecheck } from "../data";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { RateComponentsDialog } from "../rater/RateComponentsDialog";
import { WARNING_DISPLAY_NAMES } from "../rater/warnings";
import styles from "../rater/RaterCard.module.css";
import { downloadBlob } from "../../../util";

type RaterTabProps = {
  reportId: string;
};

const STATUS_TAG_SEVERITY: Record<
  (string & ReportJobBatchEntry["outcome"]) | "none",
  "success" | "info" | "warning" | "danger" | null
> = {
  success: "success",
  none: "info",
  failure: "danger",
};

const STATUS_LABEL: Record<
  (string & ReportJobBatchEntry["outcome"]) | "none",
  string
> = {
  success: "Success",
  none: "In Progress",
  failure: "Failed",
};

const WarningGroup: React.FC<{
  severity: "error" | "warn";
  title: string;
  warnings: RatePrecheckWarning[];
}> = ({ severity, title, warnings }) => (
  <Message
    className={styles.warningMessage}
    severity={severity}
    content={
      <div className={styles.warningGroup}>
        <div className={styles.warningTitle}>{title}</div>
        <ul className={styles.warningList}>
          {warnings.map((w, i) => (
            <li key={i}>
              <div className={styles.warningSummary} title={w.summary}>
                {w.summary}
              </div>
              {w.detail && (
                <div className={styles.warningDetail} title={w.detail}>
                  {w.detail}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    }
  />
);

export const RaterTab: React.FC<RaterTabProps> = ({ reportId }) => {
  const { jobs } = useReportJobs(reportId, { integrationTypes: ["RATER"] });
  const { data: precheck } = useRatePrecheck(reportId);

  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(
    null
  );
  const [ratesBatchId, setRatesBatchId] = useState<string | null>(null);

  const blockingWarnings = precheck?.warnings.filter((w) => w.blocking) ?? [];
  const nonBlockingWarnings =
    precheck?.warnings.filter((w) => !w.blocking) ?? [];

  const sortedJobs = [...jobs].sort(
    (a, b) =>
      new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );

  const handleDownload = async (job: ReportJobBatchEntry) => {
    if (!job.batch_id) return;
    setDownloadingBatchId(job.batch_id);
    try {
      const { data, filename } = await downloadRaterWorkbook(
        reportId,
        job.batch_id
      );
      downloadBlob(filename ?? "rater.xlsx", data);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  return (
    <div className={styles.verticalContainerLargeGap}>
      {blockingWarnings.length > 0 && (
        <WarningGroup
          severity="error"
          title="Blocking issues"
          warnings={blockingWarnings}
        />
      )}
      {nonBlockingWarnings.length > 0 && (
        <WarningGroup
          severity="warn"
          title="Warnings"
          warnings={nonBlockingWarnings}
        />
      )}

      {sortedJobs.length > 0 && (
        <DataTable value={sortedJobs} size="small">
          <Column
            header="Started"
            body={(job: ReportJobBatchEntry) =>
              new Date(job.started_at + "Z").toLocaleString()
            }
          />
          <Column
            header="Status"
            body={(job: ReportJobBatchEntry) => (
              <Tag
                severity={STATUS_TAG_SEVERITY[job.outcome ?? "none"] ?? "info"}
                value={STATUS_LABEL[job.outcome ?? "none"] ?? job.outcome}
              />
            )}
          />
          <Column
            header="Template"
            body={(job: ReportJobBatchEntry) => {
              const templateName = job.jobs[0]?.response?.template_name as
                | string
                | undefined;
              return templateName ?? null;
            }}
          />
          <Column
            header="Addresses"
            body={(job: ReportJobBatchEntry) => {
              const count = job.jobs[0]?.response?.address_count as
                | number
                | undefined;
              return count ?? null;
            }}
          />
          <Column
            header="Errors/Warnings"
            body={(job: ReportJobBatchEntry) => {
              const resp = job.jobs[0]?.response;
              const errorDescription = resp?.error_description as
                | string
                | undefined;
              if (errorDescription) {
                const code = resp?.error_code as string | undefined;
                return (
                  <span style={{ color: "var(--red-500)" }} title={code}>
                    {errorDescription}
                  </span>
                );
              }
              const warnings = resp?.warnings as
                | Record<string, number>
                | undefined;
              if (!warnings || Object.keys(warnings).length === 0) return null;
              return (
                <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                  {Object.entries(warnings).map(([msg, count]) => (
                    <li key={msg}>
                      {WARNING_DISPLAY_NAMES[msg] ?? msg} ({count})
                    </li>
                  ))}
                </ul>
              );
            }}
          />
          <Column
            header=""
            body={(job: ReportJobBatchEntry) => {
              if (job.outcome !== "success") return null;
              const isDownloading = downloadingBatchId === job.batch_id;
              return (
                <div className={styles.horizontalContainer}>
                  <Button
                    icon="pi pi-percentage"
                    text
                    size="small"
                    onClick={() => setRatesBatchId(job.batch_id ?? null)}
                    tooltip="View rates"
                    tooltipOptions={{ position: "top" }}
                  />
                  <Button
                    icon="pi pi-download"
                    text
                    size="small"
                    loading={isDownloading}
                    disabled={isDownloading}
                    onClick={() => handleDownload(job)}
                    tooltip="Download rater"
                    tooltipOptions={{ position: "top" }}
                  />
                </div>
              );
            }}
          />
        </DataTable>
      )}

      <RateComponentsDialog
        reportId={reportId}
        batchId={ratesBatchId}
        visible={ratesBatchId !== null}
        onHide={() => setRatesBatchId(null)}
      />
    </div>
  );
};
